import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { api_url, environment } from "../config.js";
import axios from "axios";

const fake_token =
  "eyJhbGciOiJSUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOi9hay5leGFtcGxlLmNvbS9hdXRoL3JlYWxttV7Y3cskztDZqqmQI";

const getAuthToken = () => {
  const keycloak = JSON.parse(localStorage.getItem("keycloak") || "{}");
  return environment === "production-no-auth" ? fake_token : keycloak?.token;
};

export const listImages = createAsyncThunk(
  "image/list",
  async (_, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.get(`${api_url}/images`, config);
      return data;
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

export const imageDetails = createAsyncThunk(
  "image/details",
  async (id, { dispatch, rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.get(`${api_url}/images/${id}`, config);

      return { image: data };
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

export const imageCreate = createAsyncThunk(
  "image/create",
  async (imageData, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.post(`${api_url}/images`, imageData, config);
      return data;
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

export const imageUpdate = createAsyncThunk(
  "image/update",
  async ({ id, imageData }, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.put(
        `${api_url}/images/${id}`,
        imageData,
        config,
      );
      return data;
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

export const imageDelete = createAsyncThunk(
  "image/delete",
  async (id, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      await axios.delete(`${api_url}/images/${id}`, config);
      return id;
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

export const imageSchema = createAsyncThunk(
  "image/imageSchema",
  async (_, { rejectWithValue }) => {
    const config = {
      headers: { Authorization: `Bearer ${getAuthToken()}` },
    };

    try {
      const { data } = await axios.get(`${api_url}/openapi.json`, config);
      const schema = data.components.schemas.Image;
      return schema;
    } catch (error) {
      if (environment === "development") {
        console.error("Error :", error.message);
      }
      return rejectWithValue(
        error.response?.data?.detail || "An unexpected error occurred.",
      );
    }
  },
);

const initialState = {
  images: [],
  image: {},
  image_schema: {},
  loading: false,
  error: null,
};

const imageSlice = createSlice({
  name: "image",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // images list
      .addCase(listImages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(listImages.fulfilled, (state, action) => {
        state.loading = false;
        state.images = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.images || [];
      })

      .addCase(listImages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // image details
      .addCase(imageDetails.pending, (state) => {
        state.loading = true;
        state.image = {};
        // state.error = null;
      })
      .addCase(imageDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.image = action.payload.image;
      })
      .addCase(imageDetails.rejected, (state, action) => {
        state.loading = false;
        state.image = {};
        state.error = action.payload;
      })

      // create image
      .addCase(imageCreate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(imageCreate.fulfilled, (state, action) => {
        state.loading = false;
        state.images.push(action.payload);
      })
      .addCase(imageCreate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // update image
      .addCase(imageUpdate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(imageUpdate.fulfilled, (state, action) => {
        state.loading = false;
        state.images = state.images.map((image) =>
          image.id === action.payload.id ? action.payload : image,
        );
        state.image = action.payload;
      })
      .addCase(imageUpdate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // delete image
      .addCase(imageDelete.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(imageDelete.fulfilled, (state, action) => {
        state.loading = false;
        state.images = state.images.filter(
          (image) => image.id !== action.payload,
        );
        state.image = {};
      })
      .addCase(imageDelete.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // image schema
      .addCase(imageSchema.pending, (state) => {
        state.loading = true;
      })
      .addCase(imageSchema.fulfilled, (state, action) => {
        state.loading = false;
        state.image_schema = action.payload;
      })
      .addCase(imageSchema.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export default imageSlice.reducer;
